import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { useAuthStore } from '@/shared/auth/store';
import { useFeatures } from '@/shared/config/useFeatures';
import { usePrefState } from '@/shared/prefs/usePrefState';
import { NEW_SCREENS_PREF } from './newScreensMap';

/** What {@link useNewScreens} hands back. */
export type NewScreensState = {
  /** Is the slider on? Default true -- Chris: "default it on remember it if turned off". */
  enabled: boolean;
  setEnabled: (v: boolean) => void;
  /**
   * Is this the customer DEMO instance (ADR-0050 `PH10_DEMO_SYSTEM`)? The slider only renders
   * and the swap only applies when this is true -- on ph10-test and MYRX nothing changes.
   */
  demo: boolean;
};

/**
 * slice-3690 -- state behind the profile menu's "New Screens" slider.
 *
 * <p>The preference is a plain {@link usePrefState} key, so it is per user and per browser and
 * survives a reload, the same mechanism as the criteria pane's Auto-hide tick (dev-rules §4.25).
 * Default ON: an operator who never touches the slider gets the v2 screens.
 *
 * <p>`demo` is read through {@link useFeatures} rather than `useDemoSystemEnabled` so the hook is
 * self-sufficient -- it primes the flags query itself if nothing else has. While that first
 * request is in flight the flag reads false, which means "no swap yet", never a wrong swap.
 */
export function useNewScreens(): NewScreensState {
  const { demoSystem } = useFeatures();
  const [enabled, setPref] = usePrefState<boolean>(NEW_SCREENS_PREF, true);

  const setEnabled = useCallback((v: boolean) => setPref(v), [setPref]);

  return { enabled, setEnabled, demo: demoSystem };
}

/** slice-4481: GET/PUT /api/platform/new-screens — the tenant's v2 list (null = never saved). */
export const NEW_SCREENS_LIST_KEY = ['platform', 'new-screens'] as const;
const NEW_SCREENS_LIST_PATH = '/api/platform/new-screens';

/**
 * slice-4481 (John 29 Oct) — the tenant's list of v2 routes the slider opens. Fetched only on the
 * demo instance; {@code routes} is null until a list was saved (the swap then keeps Chris's seven).
 * Until it answers ({@code ready} false) the swap holds a bare open of a swappable route, so it
 * never sends anyone to a v2 page the tenant did not choose.
 */
export function useNewScreensList(demo: boolean): { routes: string[] | null; ready: boolean } {
  // Signed-out screens (login) never ask: there is no tenant yet, and a 401 must not bounce them.
  const userId = useAuthStore((st) => st.session?.userId ?? '');
  const on = demo && userId !== '';
  const q = useQuery<{ routes: string[] | null }>({
    queryKey: [...NEW_SCREENS_LIST_KEY, userId],
    queryFn: () => api<{ routes: string[] | null }>(NEW_SCREENS_LIST_PATH),
    enabled: on,
    staleTime: 60_000,
    retry: false,
  });
  // ready: answered (or failed — a failed read keeps the default seven, as before 4481).
  return { routes: q.data?.routes ?? null, ready: !on || !q.isPending };
}

/** slice-4481: save the whole list (ADMINISTRATOR-MENU; demo only, server-checked). */
export function useSaveNewScreensList() {
  const qc = useQueryClient();
  return useMutation<{ routes: string[] | null }, Error, string[]>({
    mutationFn: (routes) =>
      api<{ routes: string[] | null }>(NEW_SCREENS_LIST_PATH, { method: 'PUT', json: { routes } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: NEW_SCREENS_LIST_KEY }),
  });
}
